// client/src/coms/views/games_parts/useWatchActivity.js
// One Watch Together session as Activities sees it. Joining (idempotent, so it also works after a refresh
// or from the listing), the live connection to the session's server relay (watchRelayRoom.js), who is
// in it, and the host's actions. Playback itself is the existing Watch Together view over that relay.
import { useCallback, useEffect, useRef, useState } from 'react';
import { gamesApi } from './useGameSession';
import { createWatchRelayRoom } from '../watch_together_parts/watchRelayRoom';

export const WATCH_ERRORS = {
  watch_kicked: 'The host removed you from this watch session.',
  watch_banned: 'You cannot join this watch session.',
  watch_code_revoked: 'That invite code was replaced by the host. Ask for the new one.',
  watch_session_ended: 'This watch session has ended.',
  activity_not_found: 'This watch session is not available.',
  too_many_watch_sessions: 'You already host the maximum number of watch sessions. End one first.',
  watch_not_allowed: 'You do not have permission to start Watch Together in that voice channel.',
  channel_not_found: 'That voice channel is not available.',
  host_only: 'Only the host can do that.',
  not_in_session: 'That person is no longer in the session.',
  cannot_kick_owner: 'The person who started the session cannot be removed.',
  session_full: 'This watch session is full.',
};

export function describeWatchError(code, fallback = 'Something went wrong. Try again.') {
  return WATCH_ERRORS[code] || fallback;
}

export function isInVoiceChannel(voiceSession, activity) {
  if (!voiceSession || !activity || !activity.voice) return false;
  if (voiceSession.status !== 'connected' && voiceSession.status !== 'reconnecting') return false;
  return String(voiceSession.channelDbId) === String(activity.voice.channelId) && String(voiceSession.serverId) === String(activity.voice.serverPublicId);
}

export default function useWatchActivity(id) {
  const [activity, setActivity] = useState(null);
  const [gone, setGone] = useState('');
  const [room, setRoom] = useState(null);
  const [connection, setConnection] = useState('connecting');
  const [error, setError] = useState('');
  const roomRef = useRef(null);

  useEffect(() => {
    setActivity(null);
    setGone('');
    setRoom(null);
    setConnection('connecting');
    if (!id) return undefined;
    let alive = true;
    let relay = null;
    (async () => {
      const joined = await gamesApi(`/api/activities/watch/${encodeURIComponent(id)}/join`, { method: 'POST', body: {} }).catch(() => null);
      if (!alive) return;
      if (!joined || !joined.ok) {
        setGone((joined && joined.data && joined.data.error) || 'activity_not_found');
        return;
      }
      setActivity(joined.data.activity);
      relay = createWatchRelayRoom(id, {
        onActivity: (next) => alive && setActivity(next),
        onEnded: () => alive && setGone('watch_session_ended'),
        onKicked: () => alive && setGone('watch_kicked'),
        onConnection: async (state) => {
          if (!alive) return;
          setConnection(state);
          // The server refused the stream: find out why (ended, removed, ...).
          if (state === 'closed' && relay && !relay.localParticipant) {
            const check = await gamesApi(`/api/activities/watch/${encodeURIComponent(id)}`).catch(() => null);
            if (alive && check && !check.ok) setGone((check.data && check.data.error) || 'activity_not_found');
          }
        },
      });
      roomRef.current = relay;
      setRoom(relay);
      relay.connect();
    })();
    return () => {
      alive = false;
      if (relay) relay.disconnect();
      roomRef.current = null;
    };
  }, [id]);

  const post = useCallback(async (path, body = {}) => {
    setError('');
    const result = await gamesApi(`/api/activities/watch/${encodeURIComponent(id)}${path}`, { method: 'POST', body }).catch(() => null);
    if (result && result.ok) {
      if (result.data && result.data.activity) setActivity(result.data.activity);
      return true;
    }
    setError(describeWatchError(result && result.data && result.data.error));
    return false;
  }, [id]);

  const leave = useCallback(async () => {
    await post('/leave');
    if (roomRef.current) roomRef.current.disconnect();
  }, [post]);

  const end = useCallback(async () => {
    const ok = await post('/end');
    if (ok && roomRef.current) roomRef.current.disconnect();
    return ok;
  }, [post]);

  return {
    activity,
    room,
    gone,
    connection,
    error,
    clearError: () => setError(''),
    leave,
    end,
    kick: (userId) => post('/kick', { userId }),
    transfer: (userId) => post('/transfer', { userId }),
    setSettings: (settings) => post('/settings', settings),
    setAudience: (audience) => post('/settings', { audience }),
    resetCode: () => post('/code'),
  };
}
